import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Home from "./pages/Home.tsx";
import { AdminAuthProvider } from "./admin/AuthContext";
import ScrollToTop from "./components/ScrollToTop";

// Retry dynamic imports once after a stale-chunk failure (post-deploy).
// If a chunk hash no longer exists, force a one-time hard reload so the
// browser fetches the new index.html + manifest.
const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    const KEY = "lovable:chunk-reloaded";
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(KEY); } catch {}
      return mod;
    } catch (err: any) {
      const msg = String(err?.message || err || "");
      const isChunkErr =
        /dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|error loading/i.test(
          msg
        );
      if (isChunkErr && typeof window !== "undefined") {
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, "1");
          window.location.reload();
          return new Promise(() => {}) as any;
        }
      }
      throw err;
    }
  });

// Lazy-load all non-landing routes to reduce initial JS bundle size
const Index = lazyWithRetry(() => import("./pages/Index.tsx"));
const ToolDetail = lazyWithRetry(() => import("./pages/ToolDetail.tsx"));
const CoFound = lazyWithRetry(() => import("./pages/CoFound.tsx"));
const About = lazyWithRetry(() => import("./pages/About.tsx"));
const Blog = lazyWithRetry(() => import("./pages/Blog.tsx"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost.tsx"));
const Privacy = lazyWithRetry(() => import("./pages/legal/Privacy.tsx"));
const Terms = lazyWithRetry(() => import("./pages/legal/Terms.tsx"));
const Affiliates = lazyWithRetry(() => import("./pages/legal/Affiliates.tsx"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound.tsx"));
const Contact = lazyWithRetry(() => import("./pages/Contact.tsx"));
const Chapter1 = lazyWithRetry(() => import("./pages/Chapter1.tsx"));
const PlaybookIHaveIdea = lazyWithRetry(() => import("./pages/PlaybookIHaveIdea.tsx"));
const PlaybookNoIdea = lazyWithRetry(() => import("./pages/PlaybookNoIdea.tsx"));
const PlaybookIdeaDetail = lazyWithRetry(() => import("./pages/PlaybookIdeaDetail.tsx"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe.tsx"));
const Builders = lazyWithRetry(() => import("./pages/Builders.tsx"));
const BuilderDetail = lazyWithRetry(() => import("./pages/BuilderDetail.tsx"));
const FundLibrary = lazyWithRetry(() => import("./pages/FundLibrary.tsx"));
const FundDetail = lazyWithRetry(() => import("./pages/FundDetail.tsx"));
const ThePledge = lazyWithRetry(() => import("./pages/ThePledge.tsx"));
const AdminLayout = lazyWithRetry(() => import("./admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const Dashboard = lazyWithRetry(() => import("./admin/pages/Dashboard"));
const ToolsManager = lazyWithRetry(() => import("./admin/pages/ToolsManager"));
const BlogManagerPage = lazyWithRetry(() => import("./admin/pages/BlogManager"));
const FundsManager = lazyWithRetry(() => import("./admin/pages/FundsManager"));
const IdeasManager = lazyWithRetry(() => import("./admin/pages/IdeasManager"));
const BuildersManager = lazyWithRetry(() => import("./admin/pages/BuildersManager"));
const AiCourses = lazyWithRetry(() => import("./pages/AiCourses.tsx"));
const AiCourseDetail = lazyWithRetry(() => import("./pages/AiCourseDetail.tsx"));
const AiCoursesManager = lazyWithRetry(() => import("./admin/pages/AiCoursesManager"));
const PublicFundingManager = lazyWithRetry(() => import("./admin/pages/PublicFundingManager"));
const PitchDeckTemplatesManager = lazyWithRetry(() => import("./admin/pages/PitchDeckTemplatesManager"));
const GrantsSchemes = lazyWithRetry(() => import("./pages/GrantsSchemes.tsx"));
const GrantsSchemesDetail = lazyWithRetry(() => import("./pages/GrantsSchemesDetail.tsx"));
const PitchDeckBuilder = lazyWithRetry(() => import("./pages/PitchDeckBuilder.tsx"));
const PitchDeckDetail = lazyWithRetry(() => import("./pages/PitchDeckDetail.tsx"));


const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AdminAuthProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/tools" element={<Index />} />
              <Route path="/tools/ai-courses" element={<AiCourses />} />
              <Route path="/tools/ai-courses/:slug" element={<AiCourseDetail />} />
              <Route path="/tools/:slug" element={<ToolDetail />} />
              <Route path="/co-found" element={<CoFound />} />
              <Route path="/co-found/fund-library" element={<FundLibrary />} />
              <Route path="/co-found/fund-library/:slug" element={<FundDetail />} />
              <Route path="/co-found/the-pledge" element={<ThePledge />} />
              <Route path="/about" element={<About />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/affiliates" element={<Affiliates />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/chapter-1" element={<Chapter1 />} />
              <Route path="/playbook/i-have-an-idea" element={<PlaybookIHaveIdea />} />
              <Route path="/playbook/no-idea" element={<PlaybookNoIdea />} />
              <Route path="/playbook/no-idea/:slug" element={<PlaybookIdeaDetail />} />
              <Route path="/builders" element={<Builders />} />
              <Route path="/builders/:slug" element={<BuilderDetail />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/co-found/grants-and-schemes" element={<GrantsSchemes />} />
              <Route path="/co-found/grants-and-schemes/:slug" element={<GrantsSchemesDetail />} />
              <Route path="/co-found/pitch-deck-builder" element={<PitchDeckBuilder />} />
              <Route path="/co-found/pitch-deck-builder/:slug" element={<PitchDeckDetail />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="tools" element={<ToolsManager />} />
                <Route path="blog" element={<BlogManagerPage />} />
                <Route path="funds" element={<FundsManager />} />
                <Route path="private-capital" element={<FundsManager />} />
                <Route path="public-funding" element={<PublicFundingManager />} />
                <Route path="pitch-decks" element={<PitchDeckTemplatesManager />} />
                <Route path="ideas" element={<IdeasManager />} />
                <Route path="builders" element={<BuildersManager />} />
                <Route path="ai-courses" element={<AiCoursesManager />} />
                <Route path="ai-programs" element={<AiCoursesManager />} />
              </Route>
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
